/**
 * Art-directed generated visuals — BUILD-PLAN §5, §74.
 *
 * §74 requires the design to "remain visually complete even before final
 * photography is uploaded", using "branded" and "editorial" placeholders
 * rather than grey boxes.
 *
 * These are composed illustrations in the brand's own language — parcel,
 * ribbon, arch, monogram — drawn in ivory and gold with fine linework. They
 * read as deliberate art direction rather than a missing asset, which is what
 * lets the site be shown to a client before a photoshoot exists.
 *
 * They are deliberately NOT photorealistic. §86 forbids faking content, so
 * these must never be mistaken for a photograph of a real product.
 *
 * Every composition is deterministic from its `seed`, so a given product
 * always renders the same artwork across reloads and pagination.
 */

type Motif = 'parcel' | 'arch' | 'ribbon' | 'monogram';

const MOTIFS: Motif[] = ['parcel', 'arch', 'ribbon', 'monogram'];

/**
 * Colourways — the box finishes a customer can choose (§14 variants).
 *
 * Deliberately led by the dark finishes. Luxury packaging gets its presence
 * from contrast: gold foil on near-black board reads as expensive, whereas
 * pale gold on ivory disappears into its own background. An ivory-only
 * palette was why the catalogue previously looked washed out.
 *
 * `label` is customer-facing — these double as the swatch names.
 */
export const COLOURWAYS = [
  { id: 'onyx', label: 'Onyx', base: '#211C18', deep: '#0D0B09', ink: '#EBD3A0', gold: '#C9A253', dark: true },
  { id: 'noir', label: 'Noir & Gold', base: '#2B2520', deep: '#141110', ink: '#E8CE93', gold: '#BB913D', dark: true },
  { id: 'forest', label: 'Deep Forest', base: '#22302A', deep: '#131A16', ink: '#DCC994', gold: '#B8974A', dark: true },
  { id: 'burgundy', label: 'Burgundy', base: '#40222A', deep: '#241014', ink: '#E7CB96', gold: '#BB913D', dark: true },
  { id: 'champagne', label: 'Champagne', base: '#E9D9C2', deep: '#D2B893', ink: '#6F5420', gold: '#A87F32', dark: false },
  { id: 'ivory', label: 'Ivory', base: '#F7EDE1', deep: '#E4D2BB', ink: '#8F6F2E', gold: '#BB913D', dark: false },
] as const;

export type ColourwayId = (typeof COLOURWAYS)[number]['id'];

const PALETTES = COLOURWAYS;

/** djb2 — only needs an even spread across a few buckets. */
function hash(seed: string): number {
  let value = 5381;
  for (let i = 0; i < seed.length; i++) {
    value = ((value << 5) + value + seed.charCodeAt(i)) >>> 0;
  }

  return value;
}

function initials(name: string): string {
  const words = name.replace(/^The\s+/i, '').split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'GL';

  return words.slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('');
}

interface BrandArtworkProps {
  /** Drives motif, palette and monogram. */
  seed: string;
  /** Force a motif instead of deriving it — used for hero panels. */
  motif?: Motif;
  /**
   * Force a colourway instead of deriving it from the seed.
   *
   * This is what makes the swatches work: selecting a finish re-renders the
   * same artwork in that colourway, so the customer sees the choice applied
   * rather than described (§14 variants, §16 personalisation preview).
   */
  colourway?: ColourwayId;
  /** Portrait suits product cards; square suits galleries; wide suits heroes. */
  ratio?: 'portrait' | 'square' | 'wide';
  className?: string;
}

const VIEWBOX = {
  portrait: { w: 400, h: 500 },
  square: { w: 500, h: 500 },
  wide: { w: 800, h: 560 },
} as const;

export function BrandArtwork({
  seed,
  motif,
  colourway,
  ratio = 'portrait',
  className = '',
}: BrandArtworkProps) {
  const h = hash(seed);

  const palette = colourway
    ? PALETTES.find((entry) => entry.id === colourway) ?? PALETTES[h % PALETTES.length]!
    : PALETTES[h % PALETTES.length]!;
  /*
   | `>>>`, not `>>`.
   |
   | hash() returns an UNSIGNED 32-bit value (it ends in `>>> 0`), so roughly
   | half of all seeds exceed 2^31. The signed shift `>>` coerces its operand
   | to int32 first, which turns every one of those into a negative number —
   | and a negative modulo stays negative in JavaScript, so this indexed
   | MOTIFS[-1], got `undefined`, and rendered NO motif whatsoever.
   |
   | The failure was invisible in code and silent at runtime: the ground,
   | weave, vignette and keyline all still drew, so the artwork looked like a
   | correctly rendered empty box rather than a bug. On the homepage it read
   | as product images failing to load. The unsigned shift keeps the value in
   | range and every seed picks a real motif.
   */
  const chosen = motif ?? MOTIFS[(h >>> 3) % MOTIFS.length]!;
  const { w, h: vh } = VIEWBOX[ratio];
  const uid = `art-${h.toString(36)}`;
  const mark = initials(seed);

  return (
    <div aria-hidden="true" className={`h-full w-full overflow-hidden ${className}`}>
      <svg
        viewBox={`0 0 ${w} ${vh}`}
        preserveAspectRatio="xMidYMid slice"
        className="h-full w-full"
        focusable="false"
      >
        <defs>
          {/* Soft diagonal ground — light falling across a surface. */}
          <linearGradient id={`${uid}-ground`} x1="0.1" y1="0" x2="0.75" y2="1">
            <stop offset="0%" stopColor={palette.base} />
            <stop offset="100%" stopColor={palette.deep} />
          </linearGradient>

          {/* Gold leaf: a narrow bright band, not a full metallic gradient —
              restraint is what keeps it from looking cheap (§5). */}
          <linearGradient id={`${uid}-leaf`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={palette.gold} stopOpacity="0.55" />
            <stop offset="45%" stopColor="#E8CE93" stopOpacity="0.9" />
            <stop offset="55%" stopColor={palette.gold} stopOpacity="0.75" />
            <stop offset="100%" stopColor={palette.ink} stopOpacity="0.5" />
          </linearGradient>

          {/* Warm vignette, tightening focus toward the centre. */}
          <radialGradient id={`${uid}-vignette`} cx="0.5" cy="0.45" r="0.75">
            <stop offset="55%" stopColor="#000000" stopOpacity="0" />
            <stop offset="100%" stopColor="#5B4A32" stopOpacity="0.13" />
          </radialGradient>

          {/* Fine weave, suggesting paper stock or linen at a glance. */}
          <pattern
            id={`${uid}-weave`}
            width="7"
            height="7"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <line x1="0" y1="0" x2="0" y2="7" stroke={palette.gold} strokeWidth="0.4" opacity={palette.dark ? 0.14 : 0.1} />
          </pattern>

          <filter id={`${uid}-soft`} x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="10" stdDeviation="14" floodColor="#4A3A22" floodOpacity="0.16" />
          </filter>
        </defs>

        <rect width={w} height={vh} fill={`url(#${uid}-ground)`} />
        <rect width={w} height={vh} fill={`url(#${uid}-weave)`} />

        <g transform={`translate(${w / 2} ${vh / 2})`}>
          {chosen === 'parcel' && <Parcel uid={uid} palette={palette} />}
          {chosen === 'arch' && <Arch uid={uid} palette={palette} mark={mark} />}
          {chosen === 'ribbon' && <Ribbon uid={uid} palette={palette} />}
          {chosen === 'monogram' && <Monogram palette={palette} mark={mark} />}
        </g>

        <rect width={w} height={vh} fill={`url(#${uid}-vignette)`} />

        {/* Inset keyline echoing the boxed presentation of the products. */}
        <rect
          x="22"
          y="22"
          width={w - 44}
          height={vh - 44}
          fill="none"
          stroke={palette.gold}
          strokeWidth="0.75"
          opacity={palette.dark ? 0.5 : 0.3}
        />
      </svg>
    </div>
  );
}

type Palette = (typeof PALETTES)[number];

/** A wrapped gift box seen face-on, with ribbon and bow loops. */
function Parcel({ uid, palette }: { uid: string; palette: Palette }) {
  return (
    <g filter={`url(#${uid}-soft)`}>
      {/*
        Opacity on the dark colourways used to be 0.2 against a #141110
        ground, which is a difference of a few levels of grey — the shape was
        drawn, sized and positioned correctly and simply could not be seen.
        Product cards on the homepage came out as black rectangles, which read
        as images failing to load rather than as artwork.

        Dark grounds need MORE of the light shape, not less. The values below
        are lower than the light-palette ones because the contrast ratio is
        already higher, but high enough that the motif always reads.
      */}
      <rect x="-105" y="-80" width="210" height="165" rx="3" fill="#FFFFFF" opacity={palette.dark ? 0.5 : 0.72} />
      <rect
        x="-105"
        y="-80"
        width="210"
        height="165"
        rx="3"
        fill="none"
        stroke={palette.gold}
        strokeWidth="0.9"
        opacity={palette.dark ? 0.55 : 0.35}
      />

      {/* Lid line */}
      <line x1="-105" y1="-40" x2="105" y2="-40" stroke={palette.gold} strokeWidth="0.8" opacity={palette.dark ? 0.45 : 0.28} />

      {/* Ribbon cross */}
      <rect x="-16" y="-80" width="32" height="165" fill={`url(#${uid}-leaf)`} opacity="0.85" />
      <rect x="-105" y="-14" width="210" height="26" fill={`url(#${uid}-leaf)`} opacity="0.72" />

      {/* Bow loops */}
      <path
        d="M0 -80 C -52 -122, -86 -96, -60 -74 C -44 -61, -16 -70, 0 -80 Z"
        fill={`url(#${uid}-leaf)`}
        opacity="0.9"
      />
      <path
        d="M0 -80 C 52 -122, 86 -96, 60 -74 C 44 -61, 16 -70, 0 -80 Z"
        fill={`url(#${uid}-leaf)`}
        opacity="0.9"
      />
      <circle cx="0" cy="-80" r="8" fill={palette.gold} opacity="0.95" />
    </g>
  );
}

/** A slender arch — the "portal" motif common to luxury editorial layouts. */
function Arch({ uid, palette, mark }: { uid: string; palette: Palette; mark: string }) {
  return (
    <g>
      <path
        d="M-92 110 L-92 -18 A92 92 0 0 1 92 -18 L92 110 Z"
        fill="#FFFFFF"
        opacity={palette.dark ? 0.44 : 0.6}
        filter={`url(#${uid}-soft)`}
      />
      <path
        d="M-92 110 L-92 -18 A92 92 0 0 1 92 -18 L92 110"
        fill="none"
        stroke={`url(#${uid}-leaf)`}
        strokeWidth="2.5"
      />
      <path
        d="M-62 110 L-62 -12 A62 62 0 0 1 62 -12 L62 110"
        fill="none"
        stroke={palette.ink}
        strokeWidth="0.7"
        opacity="0.32"
      />

      <text
        x="0"
        y="42"
        textAnchor="middle"
        fill={palette.ink}
        opacity={palette.dark ? 0.95 : 0.75}
        style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: '68px',
          fontWeight: 300,
          letterSpacing: '0.06em',
        }}
      >
        {mark}
      </text>
    </g>
  );
}

/** A flowing ribbon — motion and craft, the packaging made abstract. */
function Ribbon({ uid, palette }: { uid: string; palette: Palette }) {
  return (
    <g>
      <path
        d="M-150 60 C -80 -60, -20 120, 40 0 S 130 -70, 155 -30"
        fill="none"
        stroke={`url(#${uid}-leaf)`}
        strokeWidth="18"
        strokeLinecap="round"
        opacity={palette.dark ? 1 : 0.85}
        filter={`url(#${uid}-soft)`}
      />
      <path
        d="M-150 88 C -80 -32, -20 148, 40 28 S 130 -42, 155 -2"
        fill="none"
        stroke={palette.gold}
        strokeWidth="0.8"
        opacity={palette.dark ? 0.5 : 0.3}
      />
      <circle cx="40" cy="0" r="6" fill={palette.gold} opacity="0.9" />
    </g>
  );
}

/** Oversized letterform, set as a masthead would be. */
function Monogram({ palette, mark }: { palette: Palette; mark: string }) {
  return (
    <g>
      <circle cx="0" cy="-6" r="104" fill="#FFFFFF" opacity={palette.dark ? 0.36 : 0.42} />
      <circle cx="0" cy="-6" r="104" fill="none" stroke={palette.gold} strokeWidth="0.9" opacity="0.5" />
      <circle cx="0" cy="-6" r="88" fill="none" stroke={palette.ink} strokeWidth="0.6" opacity="0.25" />

      <text
        x="0"
        y="24"
        textAnchor="middle"
        fill={palette.ink}
        opacity={palette.dark ? 0.95 : 0.8}
        style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: '96px',
          fontWeight: 300,
          letterSpacing: '0.04em',
        }}
      >
        {mark}
      </text>

      <line x1="-46" y1="52" x2="46" y2="52" stroke={palette.gold} strokeWidth="0.9" opacity="0.7" />
      <text
        x="0"
        y="76"
        textAnchor="middle"
        fill={palette.ink}
        opacity={palette.dark ? 0.85 : 0.65}
        style={{
          fontFamily: "'Inter', system-ui, sans-serif",
          fontSize: '10px',
          letterSpacing: '0.45em',
        }}
      >
        GLANZ LUXE
      </text>
    </g>
  );
}
